import { ColumnType } from 'antd/es/table';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { getFullName } from '@sonar-software/sonar-ts-lib';
import React from 'react';
import { SonarText } from '@ui/text';
import { UserIssuerIcon } from 'src/js/admin-dashboard/panes/customer-view/panels/users-panel/UserIssuerIcon';
import { UserViewType } from './UsersTable';

import { Button, Checkbox, Space } from 'antd';
import { LockOutlined, MailOutlined, UnlockOutlined } from '@ant-design/icons';
import { UserWithIssuerType, UserWithMetadata } from '@sonar-software/sonar-core';

type ColumnDefinition = ColumnType<UserWithMetadata>;
type ColumnClickHandler = (record: UserWithMetadata) => void;

export function getUserNameColumn(currentUserId: number): ColumnDefinition {
	return {
		key: 'name',
		render: (_, user) => {
			const fullName = getFullName(user.firstName, user.lastName);

			return currentUserId === user.id ? `${fullName} (You)` : fullName;
		},
		title: COPY_SPECIFIC.NAME,
		width: '20%'
	};
}

export const userEmailColumn: ColumnDefinition = {
	dataIndex: 'email',
	key: 'email',
	title: COPY_SPECIFIC.EMAIL,
	width: '200px'
};

export function getUserIssuerColumn(viewType: UserViewType): ColumnDefinition {
	return {
		align: 'center',
		dataIndex: 'issuers',
		key: 'issuers',
		render: (_, user: UserWithIssuerType) => {
			let filteredIssuers = user.issuers;

			if (viewType === 'invited') {
				filteredIssuers = user.issuers.filter((x) => x === 'sonar');
			}

			if (viewType === 'active' && user.isInvited) {
				filteredIssuers = user.issuers.filter((x) => x !== 'sonar');
			}

			return (
				<Space>
					{ filteredIssuers
						.map((issuer) =>
							<UserIssuerIcon
								issuerType={ issuer }
								key={ issuer } />) }
				</Space>
			);
		},
		title: COPY_SPECIFIC.ISSUER,
		width: '70px'
	};
};

export function getUserReadOnlyColumn(
	currentUserId: number,
	disabled: boolean,
	clickHandler: ColumnClickHandler
): ColumnDefinition {
	return {
		className: 'center-align-cell',
		dataIndex: 'isReadOnly',
		key: 'isReadOnly',
		render: (_, user) => {
			const { id, isReadOnly } = user;

			return (
				<Checkbox
					checked={ isReadOnly }
					disabled={ disabled || currentUserId === id }
					onChange={ () => clickHandler(user) } />
			);
		},
		title: COPY_SPECIFIC.READ_ONLY,
		width: '70px'
	};
}

export function getUserStatusColumn(
	currentUserId: number,
	disabled: boolean,
	inviteClickHandler: ColumnClickHandler,
	lockToggleHandler: (id: number, lock: boolean) => void
): ColumnDefinition {
	return {
		className: 'center-align-cell',
		key: 'status',
		render: (_, user) => {
			const { id, isInvited, isLocked } = user;

			if (currentUserId === id) {
				return null;
			}

			if (isLocked) {
				return (
					<Button
						disabled={ disabled }
						icon={ <LockOutlined /> }
						onClick={ () => lockToggleHandler(user.id, false) }>
						{ COPY_SPECIFIC.UNLOCK_USER }
					</Button>
				);
			}

			if (isInvited) {
				return (
					<Button
						disabled={ disabled }
						icon={ <MailOutlined /> }
						onClick={ () => inviteClickHandler(user) }>
						{ COPY_SPECIFIC.RESEND_INVITE }
					</Button>
				);
			}

			if (!isLocked) {
				return (
					<Button
						disabled={ disabled }
						icon={ <UnlockOutlined /> }
						onClick={ () => lockToggleHandler(user.id, true) }>
						{ COPY_SPECIFIC.LOCK_USER }
					</Button>
				);
			}

			return null;
		},
		width: '115px'
	};
}

export function getUserOwnerCountColumn(disabled: boolean): ColumnDefinition {
	return {
		align: 'right',
		dataIndex: 'ownerCount',
		key: 'ownerCount',
		render: (_, user) => <SonarText disabled={ disabled }>{ user.ownerCount?.toLocaleString() }</SonarText>,
		title: COPY_SPECIFIC.ENTITY_OWNER,
		width: '120px'
	};
}

export function getUserStakeholderCountColumn(disabled: boolean): ColumnDefinition {
	return {
		align: 'right',
		dataIndex: 'stakeholderCount',
		key: 'stakeholderCount',
		render: (_, user) => <SonarText disabled={ disabled }>{ user.stakeholderCount?.toLocaleString() }</SonarText>,
		title: COPY_SPECIFIC.ENTITY_STAKEHOLDER,
		width: '120px'
	};
}
